import React, { useState } from 'react';
import { FaTimes, FaQrcode, FaCheck, FaTimes as FaTimesCircle, FaUser, FaCalendarAlt, FaMapMarkerAlt, FaTicketAlt, FaCamera, FaKeyboard } from 'react-icons/fa';
import { Scanner } from '@yudiel/react-qr-scanner'; // ✅ Cambio aquí

import { validateTicket, markTicketAsUsedByCode } from '../../../../../components/http/Actions/TicketsAction';

const TicketScannerModal = ({ onClose, showNotification }) => {
    const [codigo, setCodigo] = useState('');
    const [validationResult, setValidationResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showQrReader, setShowQrReader] = useState(false);
    const [qrError, setQrError] = useState('');

    const token = localStorage.getItem("token");

    const handleValidate = async (ticketCode = null) => {
        const codeToValidate = ticketCode || codigo.trim();
        
        if (!codeToValidate) {
            setError('Ingresa un código de ticket');
            return;
        }

        setLoading(true);
        setError('');
        setQrError('');
        
        try {
            await validateTicket(
                codeToValidate,
                setValidationResult,
                setError,
                token
            );
            
            // Si se escaneó un QR, actualizar también el input
            if (ticketCode) {
                setCodigo(ticketCode);
            }
        } catch (error) {
            console.error('Error validating ticket:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUseTicket = async () => {
        if (!codigo.trim()) return;

        setLoading(true);
        try {
            await markTicketAsUsedByCode(codigo.trim(), setError, token);
            showNotification('Ticket utilizado exitosamente', 'success');
            
            // Revalidar para mostrar el estado actualizado
            await validateTicket(
                codigo.trim(),
                setValidationResult,
                setError,
                token
            );
        } catch (error) {
            console.error('Error using ticket:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleQrScan = (result) => {
        if (result?.length > 0) {
            const scannedCode = result[0].rawValue;
            setShowQrReader(false);
            setQrError('');
            // Validar automáticamente el código escaneado
            handleValidate(scannedCode);
        }
    };

    const handleQrError = (error) => {
        console.error('QR Error:', error);
        setQrError('Error al acceder a la cámara. Asegúrate de dar permisos de cámara al navegador.');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('es-CL', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(price);
    };

    const clearForm = () => {
        setCodigo('');
        setValidationResult(null);
        setError('');
        setQrError('');
        setShowQrReader(false);
    };

    const toggleQrReader = () => {
        setShowQrReader(!showQrReader);
        setQrError('');
        setError('');
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto backdrop-blur-sm">
            <div className="bg-zinc-800 rounded-xl shadow-2xl max-w-2xl w-full p-8 my-8 border border-zinc-700 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold text-white flex items-center">
                        <FaQrcode className="mr-3 text-red-500" />
                        Validar Ticket
                    </h3>
                    <button 
                        onClick={onClose} 
                        className="text-gray-400 hover:text-white hover:bg-zinc-700 p-2 rounded-full transition-all duration-200"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Método de entrada */}
                <div className="mb-6">
                    <div className="flex gap-3 mb-4">
                        <button
                            onClick={toggleQrReader}
                            className={`flex-1 py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center font-medium ${
                                showQrReader 
                                    ? 'bg-red-600 text-white' 
                                    : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                            }`}
                        >
                            <FaCamera className="mr-2" />
                            {showQrReader ? 'Cerrar Cámara' : 'Escanear QR'}
                        </button>
                        <button
                            onClick={() => setShowQrReader(false)}
                            className={`flex-1 py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center font-medium ${
                                !showQrReader 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                            }`}
                        >
                            <FaKeyboard className="mr-2" />
                            Escribir Código
                        </button>
                    </div>

                    {/* Lector QR */}
                    {showQrReader && (
                        <div className="mb-4">
                            <div className="bg-zinc-900 p-4 rounded-lg">
                                <div className="aspect-square max-w-sm mx-auto overflow-hidden rounded-lg">
                                    <Scanner
                                        onScan={handleQrScan}
                                        onError={handleQrError}
                                        constraints={{
                                            facingMode: 'environment'
                                        }}
                                        styles={{
                                            container: { width: '100%', height: '100%' },
                                            video: { width: '100%', height: '100%', objectFit: 'cover' }
                                        }}
                                        components={{
                                            audio: false,
                                            finder: true
                                        }}
                                        allowMultiple={false}
                                    />
                                </div>
                                <p className="text-center text-gray-400 text-sm mt-3">
                                    📱 Apunta la cámara hacia el código QR del ticket
                                </p>
                                <p className="text-center text-gray-500 text-xs mt-1">
                                    El código se validará automáticamente al escanearlo
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Input manual */}
                    {!showQrReader && (
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Código del Ticket
                            </label>
                            <div className="flex gap-3">
                                <input
                                    type="text"
                                    value={codigo}
                                    onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                                    className="flex-1 px-4 py-3 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 font-mono"
                                    placeholder="Ej: ABC123XYZ0"
                                    onKeyPress={(e) => e.key === 'Enter' && handleValidate()}
                                />
                                <button
                                    onClick={() => handleValidate()}
                                    disabled={loading || !codigo.trim()}
                                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                >
                                    {loading ? 'Validando...' : 'Validar'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Error de QR */}
                {qrError && (
                    <div className="bg-orange-500/20 border border-orange-500 text-orange-100 px-4 py-3 rounded-lg mb-6">
                        <p className="text-sm">{qrError}</p>
                        <p className="text-xs mt-1">
                            💡 Tip: Asegúrate de permitir el acceso a la cámara cuando el navegador lo solicite
                        </p>
                    </div>
                )}

                {/* Error general */}
                {error && (
                    <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mb-6">
                        <p className="text-sm">{error}</p>
                    </div>
                )}

                {/* Resultado de validación */}
                {validationResult && (
                    <div className={`rounded-lg p-6 border ${
                        validationResult.valid 
                            ? 'bg-green-900/20 border-green-600/50' 
                            : 'bg-red-900/20 border-red-600/50'
                    }`}>
                        <div className="flex items-center mb-4">
                            {validationResult.valid ? (
                                <FaCheck className="text-green-400 text-2xl mr-3" />
                            ) : (
                                <FaTimesCircle className="text-red-400 text-2xl mr-3" />
                            )}
                            <div>
                                <h4 className={`text-lg font-semibold ${
                                    validationResult.valid ? 'text-green-300' : 'text-red-300'
                                }`}>
                                    {validationResult.valid ? 'Ticket Válido' : 'Ticket Inválido'}
                                </h4>
                                <p className="text-sm text-gray-400">{validationResult.message}</p>
                            </div>
                        </div>

                        {validationResult.ticket && (
                            <div className="bg-zinc-700/50 rounded-lg p-4 space-y-4">
                                {/* Información del evento */}
                                <div className="border-b border-zinc-600 pb-4">
                                    <div className="flex items-center mb-2">
                                        <FaTicketAlt className="text-red-500 mr-2" />
                                        <span className="font-semibold text-white">Información del Evento</span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <span className="text-gray-400">Evento:</span>
                                            <div className="font-medium text-white">{validationResult.ticket.evento.nombre}</div>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">Fecha:</span>
                                            <div className="font-medium text-white">{formatDate(validationResult.ticket.evento.fecha)}</div>
                                        </div>
                                        <div className="md:col-span-2">
                                            <span className="text-gray-400">Lugar:</span>
                                            <div className="font-medium text-white">{validationResult.ticket.evento.lugar}</div>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">Estado del Evento:</span>
                                            <span className={`px-2 py-1 rounded text-xs ${
                                                validationResult.ticket.evento.habilitado 
                                                    ? 'bg-green-600/20 text-green-300' 
                                                    : 'bg-red-600/20 text-red-300'
                                            }`}>
                                                {validationResult.ticket.evento.habilitado ? 'Habilitado' : 'Deshabilitado'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Información del ticket */}
                                <div className="border-b border-zinc-600 pb-4">
                                    <div className="flex items-center mb-2">
                                        <FaUser className="text-blue-500 mr-2" />
                                        <span className="font-semibold text-white">Información del Ticket</span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <span className="text-gray-400">Usuario:</span>
                                            <div className="font-medium text-white">
                                                {validationResult.ticket.usuario?.nombre} {validationResult.ticket.usuario?.apellido}
                                            </div>
                                            <div className="text-xs text-gray-400">{validationResult.ticket.usuario?.email}</div>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">Sección:</span>
                                            <div className="font-medium text-white">{validationResult.ticket.seccionEvento?.nombreSeccion}</div>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">Precio:</span>
                                            <div className="font-medium text-white">{formatPrice(validationResult.ticket.precio)}</div>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">Estado:</span>
                                            <span className={`px-2 py-1 rounded text-xs ${
                                                validationResult.ticket.estadoTicket === 'Activo' ? 'bg-green-600/20 text-green-300' :
                                                validationResult.ticket.estadoTicket === 'Usado' ? 'bg-blue-600/20 text-blue-300' :
                                                'bg-red-600/20 text-red-300'
                                            }`}>
                                                {validationResult.ticket.estadoTicket}
                                            </span>
                                        </div>
                                        <div className="md:col-span-2">
                                            <span className="text-gray-400">Código:</span>
                                            <div className="font-mono text-sm bg-zinc-600 px-2 py-1 rounded inline-block">
                                                {validationResult.ticket.codigoTicket}
                                            </div>
                                        </div>
                                        {validationResult.ticket.fechaUso && (
                                            <div className="md:col-span-2">
                                                <span className="text-gray-400">Fecha de uso:</span>
                                                <div className="font-medium text-blue-300">{formatDate(validationResult.ticket.fechaUso)}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Botón para usar ticket */}
                                {validationResult.valid && validationResult.ticket.estadoTicket === 'Activo' && (
                                    <div className="pt-2">
                                        <button
                                            onClick={handleUseTicket}
                                            disabled={loading}
                                            className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg transition-colors disabled:opacity-50 font-medium flex items-center justify-center"
                                        >
                                            <FaCheck className="mr-2" />
                                            {loading ? 'Procesando...' : 'Marcar como Usado'}
                                        </button>
                                    </div>
                                )}

                                {/* Mensaje si ya fue usado */}
                                {validationResult.ticket.estadoTicket === 'Usado' && (
                                    <div className="bg-blue-900/30 border border-blue-600/50 rounded-lg p-3 text-center">
                                        <span className="text-blue-300 text-sm font-medium">
                                            ✓ Este ticket ya fue utilizado
                                        </span>
                                    </div>
                                )}

                                {/* Mensaje si el evento está deshabilitado */}
                                {!validationResult.ticket.evento.habilitado && (
                                    <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-3 text-center">
                                        <span className="text-red-300 text-sm font-medium">
                                            ⚠️ El evento está deshabilitado
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Botones de acción */}
                <div className="mt-6 flex justify-between">
                    {(validationResult || error || qrError) && (
                        <button
                            onClick={clearForm}
                            className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
                        >
                            Validar otro ticket
                        </button>
                    )}
                    
                    <button
                        onClick={onClose}
                        className="ml-auto px-4 py-2 bg-zinc-600 text-white rounded-lg hover:bg-zinc-500 transition-colors"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TicketScannerModal;